import React, { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Snackbar, Alert, Box
} from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { submitFeedback } from '../services/api';

const ISSUE_TYPES = [
  'Incorrect information',
  'Missing content',
  'Formatting / display problem',
  'Broken link',
  'Other',
];

/**
 * ReportIssueButton — lets a user report incorrect/incomplete content to the
 * custodian (REQ 3.13 / #94). Reusable across detail pages.
 *
 * @param {string} pageUrl     - URL the feedback is about (defaults to current)
 * @param {string} contentType - e.g. "hansard"
 * @param {string} contentId   - ID of the content item
 */
export default function ReportIssueButton({ pageUrl, contentType, contentId, size = 'small' }) {
  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState('Incorrect information');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const descInvalid = description.trim() === '';

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  const resetForm = () => {
    setIssueType('Incorrect information');
    setDescription('');
    setEmail('');
    setTouched(false);
  };

  const handleSubmit = async () => {
    if (descInvalid) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback({
        issue_type: issueType,
        description: description.trim(),
        page_url: pageUrl || (typeof window !== 'undefined' ? window.location.href : null),
        content_type: contentType || null,
        content_id: contentId ? String(contentId) : null,
        reporter_email: email.trim() || null,
      });
      setSuccess(true);
      setOpen(false);
      resetForm();
    } catch (e) {
      setError('Could not submit your report. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size={size}
        startIcon={<FlagOutlinedIcon />}
        onClick={() => setOpen(true)}
        sx={{ borderRadius: 2 }}
      >
        Report an issue
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="report-issue-title"
      >
        <DialogTitle id="report-issue-title">Report a content issue</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="report-issue-type-label">Issue type</InputLabel>
              <Select
                labelId="report-issue-type-label"
                label="Issue type"
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
              >
                {ISSUE_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              required
              multiline
              minRows={3}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={touched && descInvalid}
              helperText={
                touched && descInvalid
                  ? 'Please describe the problem'
                  : 'Tell us what is incorrect or missing'
              }
            />

            <TextField
              label="Your email (optional)"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText="Only if you'd like us to be able to follow up"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" role="alert" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={5000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" role="status" onClose={() => setSuccess(false)}>
          Thanks — your report has been sent to our team.
        </Alert>
      </Snackbar>
    </>
  );
}
